import React from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import {
  Radio, Users, Video, BarChart3, Shield, Settings,
  ArrowRight, Activity, Coins, FileText, CheckCircle
} from 'lucide-react';

const menuItems = [
  {
    title: 'Live Streams',
    description: 'Monitor and manage active live streams, view analytics, and control broadcasts',
    icon: Video,
    path: '/admin/streams',
    permission: 'streams.view',
    color: 'from-red-500 to-pink-600',
    stats: 'Real-time monitoring'
  },
  {
    title: 'Events & Posts',
    description: 'Review, approve or reject user-submitted events and posts before they go live',
    icon: CheckCircle,
    path: '/admin/events',
    permission: 'streams.view',
    color: 'from-amber-500 to-orange-600',
    stats: 'Content vetting'
  },
  {
    title: 'User Management',
    description: 'View, edit, suspend accounts, adjust balances, and review user activity',
    icon: Users,
    path: '/admin/users',
    permission: 'users.view',
    color: 'from-indigo-500 to-purple-600',
    stats: 'Manage accounts'
  },
  {
    title: 'Analytics & Reports',
    description: 'View platform performance, revenue reports, and generate exportable reports',
    icon: BarChart3,
    path: '/admin/analytics',
    permission: 'reports.view',
    color: 'from-emerald-500 to-teal-600',
    stats: 'Insights & data'
  },
  {
    title: 'Admin Team',
    description: 'Manage admin accounts, roles, and permissions for your team',
    icon: Shield,
    path: '/admin/team',
    permission: 'admins.view',
    color: 'from-blue-500 to-cyan-600',
    stats: 'Team access'
  },
  {
    title: 'Platform Settings',
    description: 'Configure platform settings, report templates, and system preferences',
    icon: Settings,
    path: '/admin/settings',
    permission: 'settings.view',
    color: 'from-zinc-500 to-zinc-700',
    stats: 'Configuration'
  },
];

export default function AdminHomePage() {
  const { admin, hasPermission } = useAdmin();

  const accessibleItems = menuItems.filter(item => hasPermission(item.permission));

  return (
    <div className="min-h-[80vh]" data-testid="admin-home-page">
      {/* Welcome section */}
      <div className="mb-10">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Radio className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Welcome back, {admin?.firstName}!
            </h1>
            <p className="text-zinc-500 mt-1">
              You're logged in as <span className="text-indigo-400 font-medium">{admin?.role}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Quick stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Activity className="w-4 h-4" />
            Status
          </div>
          <p className="text-white font-semibold">System Online</p>
        </div>
        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Video className="w-4 h-4" />
            Live Streams
          </div>
          <p className="text-white font-semibold">0 Active</p>
        </div>
        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Users className="w-4 h-4" />
            Your Role
          </div>
          <p className="text-white font-semibold">{admin?.role}</p>
        </div>
        <div className="bg-zinc-900/50 rounded-xl p-4 border border-zinc-800">
          <div className="flex items-center gap-2 text-zinc-400 text-sm mb-1">
            <Shield className="w-4 h-4" />
            Permissions
          </div>
          <p className="text-white font-semibold">{admin?.permissions?.length || 0} granted</p>
        </div>
      </div>

      {/* Management options */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-white mb-1" style={{ fontFamily: 'Manrope' }}>
          What would you like to manage?
        </h2>
        <p className="text-zinc-500 text-sm">Select an area to access based on your permissions</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {accessibleItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="group relative bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800 hover:border-zinc-700 transition-all hover:shadow-xl hover:shadow-black/20 hover:-translate-y-1"
            data-testid={`menu-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {/* Icon */}
            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
              <item.icon className="w-7 h-7 text-white" />
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-indigo-400 transition-colors" style={{ fontFamily: 'Manrope' }}>
              {item.title}
            </h3>
            <p className="text-zinc-500 text-sm mb-4 line-clamp-2">
              {item.description}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-600 bg-zinc-800 px-2 py-1 rounded-lg">
                {item.stats}
              </span>
              <ArrowRight className="w-5 h-5 text-zinc-600 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
        ))}
      </div>

      {/* No access message */}
      {accessibleItems.length === 0 && (
        <div className="text-center py-16">
          <Shield className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Access</h3>
          <p className="text-zinc-500">You don't have permissions to access any management areas.</p>
          <p className="text-zinc-600 text-sm mt-2">Contact a Super Admin to request access.</p>
        </div>
      )}

      {/* Help section */}
      <div className="mt-10 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-2xl p-6 border border-indigo-500/20">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
            <FileText className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h4 className="text-white font-semibold mb-1">Need Help?</h4>
            <p className="text-zinc-400 text-sm">
              Your access is determined by your role ({admin?.role}). If you need additional permissions, 
              please contact a Super Admin or check the documentation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}